import { convertToUserTimezone } from "../../../../../utils/dateService";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { flattenEvents } from "../../eventCatalog";
import { pruneBanner } from "../../controls/bannerUtils";
import {
  isDefaultTransactionTitle,
  MAX_AMOUNT_PARAM_KEY,
  PERCENT_OF_TRIGGER_EVENT_KEYS,
  PERCENT_OF_TRIGGER_PARAM_KEY,
  REMOVED_STEP_PARAM_KEYS,
  REMOVED_STEP_PARAM_RESULT_KEYS,
  RESULT_PARAM_SCHEMA,
  supportsStepBannerEditor,
  TRANSACTION_TITLE_PARAM_KEY,
} from "../../constants";
import { RESTRICTION_DISPLAY_NAMES_PARAM_KEY } from "../../controls/restrictionDisplayNames";
import {
  parseAuthoringDateMs,
  stripInactiveConditionalResultParams,
} from "../../resultParamRules";
import {
  APPLIED_OFFER_MATCHES_TYPE,
  appliedOfferToggleAvailable,
  followUpInjectedConditionTypes,
  injectedFollowUpConditionsFor,
} from "./betTargetedPresets";
import type { WizardBetTargetedPreset } from "./betTargetedPresets";
import { defaultDisplayLocations } from "./displayLocationDefaults";
import type { WizardTriggerMode } from "./wizardTypes";
import {
  compileAwardFrequency,
  inferAwardFrequencyFields,
  isCooldownCondition,
  isInformationalBannerOnly,
  withAwardFrequencyCooldown,
  type AwardFrequency,
  type AwardFrequencyUnit,
} from "./awardFrequency";
import {
  isInformResultType,
  isSeriesRunCampaign,
  type BannerIn,
  type CampaignIn,
  type CampaignOut,
  type CapSpecIn,
  type ConditionSpecIn,
  type DisplayLocation,
  type EventCategoryGroup,
  type ResultType,
  type SeriesIn,
  type SeriesOut,
  type StepSpecIn,
  type TemplateIn,
  type TemplateOut,
} from "../../types";

export type { WizardBetTargetedPreset } from "./betTargetedPresets";
export type { WizardTriggerMode } from "./wizardTypes";
export {
  APPLIED_OFFER_MATCHES_TYPE,
  BET_TARGETED_PRESET_DEFINITIONS,
  BET_TARGETED_PRESET_IDS,
  appliedOfferToggleAvailable,
  inferWizardBetTargetedPreset,
  injectedFollowUpConditionsFor,
  isWizardBetTargetedPreset,
  stepsForBetTargetedPreset,
} from "./betTargetedPresets";

/** Which contract the wizard authors — campaigns (default) or templates. */
export type WizardMode = "campaign" | "template";

/** Numeric values assigned to each priority level. */
export const PRIORITY_VALUES = {
  low: 1,
  medium: 5,
  high: 8,
} as const;

export type PriorityLevel = keyof typeof PRIORITY_VALUES;

/** Default priority level for new campaigns. */
export const DEFAULT_PRIORITY_LEVEL: PriorityLevel = "medium";

/** @deprecated Use PRIORITY_VALUES[DEFAULT_PRIORITY_LEVEL] instead. Kept for any external references. */
export const DEFAULT_CAMPAIGN_PRIORITY = PRIORITY_VALUES[DEFAULT_PRIORITY_LEVEL];

/**
 * Catalog event behind both referral trigger types. The events catalog maps
 * `user_signup` -> `users:INSERT`; the backend derives `source` server-side.
 */
export const REFERRAL_TRIGGER_EVENT_KEY = "user_signup";

/**
 * Catalog event for the just-in-time "campaign starts" trigger. Only present
 * in the events catalog when the backend flag `bonus.campaign_start_trigger`
 * is on — the wizard pill is gated on that (see `TriggerStepsStep`).
 */
export const CAMPAIGN_START_EVENT_KEY = "campaign_start";

/**
 * Wizard-level campaign type (BONENG-842). "series" constrains the flow to
 * the fixed two-step blueprint and posts through `useCreateSeries` instead
 * of `useCreateCampaign` — see `buildSeriesBody`.
 */
export type WizardCampaignType = "campaign" | "series" | "display_banner";

/** Default wizard campaign type — the pre-existing standard-campaign flow. */
export const DEFAULT_CAMPAIGN_TYPE: WizardCampaignType = "campaign";

/** The series campaign type — named so call sites compare against this
 * constant instead of the bare `"series"` string literal. */
export const SERIES_CAMPAIGN_TYPE: WizardCampaignType = "series";

/** Standalone marketing banner — no bonus result (BONENG-813). */
export const DISPLAY_BANNER_CAMPAIGN_TYPE: WizardCampaignType = "display_banner";

/** Catalog/result key for the display-banner RESULT type (backend BONENG-1041). */
export const DISPLAY_BANNER_RESULT_TYPE_KEY = "display_banner";

export const campaignTypeLabel = (type: WizardCampaignType): string => {
  if (type === SERIES_CAMPAIGN_TYPE) {
    return AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TYPE_BRAZE;
  }
  return AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TYPE_PAM;
};

/**
 * Default campaign type for a new wizard. Braze (series) is preferred
 * whenever `dynamicBonusesExternal` is on; PAM is the fallback when the
 * Braze pill is hidden.
 */
export const defaultWizardCampaignType = (
  seriesEnabled: boolean,
): WizardCampaignType =>
  seriesEnabled ? SERIES_CAMPAIGN_TYPE : DEFAULT_CAMPAIGN_TYPE;

/**
 * Create-modal source seed: require an explicit pick when both chips are
 * visible; auto-select only when a single source is available.
 */
export const initialCampaignSourceSelection = (
  seriesEnabled: boolean,
  internalEnabled: boolean,
): WizardCampaignType | undefined => {
  if (seriesEnabled && internalEnabled) return undefined;
  if (seriesEnabled) return SERIES_CAMPAIGN_TYPE;
  if (internalEnabled) return DEFAULT_CAMPAIGN_TYPE;
  return undefined;
};

export const parseWizardCampaignType = (
  value: string | null | undefined,
): WizardCampaignType | undefined => {
  if (
    value === SERIES_CAMPAIGN_TYPE ||
    value === DEFAULT_CAMPAIGN_TYPE ||
    value === DISPLAY_BANNER_CAMPAIGN_TYPE
  ) {
    return value;
  }
  return undefined;
};

/** Wizard seed values that must travel with an explicit campaign-type choice. */
export const wizardInitialValuesForCampaignType = (
  campaignType: WizardCampaignType,
): Partial<WizardFormValues> => {
  if (campaignType === SERIES_CAMPAIGN_TYPE) {
    return {
      campaign_type: SERIES_CAMPAIGN_TYPE,
      start_mode: "new",
      trigger_mode: "campaign_start",
      schedule_mode: "never",
      date_range: [null, null],
      audience_mode: "all",
      segment_id: undefined,
      steps: [{}, {}],
      bet_targeted_preset: null,
      created_from_template_id: null,
      award_frequency: "one_time",
      award_frequency_amount: "1",
      award_frequency_unit: "days",
    };
  }

  if (campaignType === DISPLAY_BANNER_CAMPAIGN_TYPE) {
    return {
      campaign_type: DISPLAY_BANNER_CAMPAIGN_TYPE,
      start_mode: "new",
      trigger_mode: "campaign_start",
      schedule_mode: DEFAULT_SCHEDULE_MODE,
      audience_mode: "all",
      segment_id: undefined,
      bet_targeted_preset: null,
      created_from_template_id: null,
      steps: [
        {
          result_type_key: DISPLAY_BANNER_RESULT_TYPE_KEY,
          display_locations: ["casino", "discover"],
          freeform_layout: DEFAULT_FREEFORM_BANNER_LAYOUT,
          banner: {
            claim: {
              visible: DEFAULT_DISPLAY_BANNER_CLAIM.visible,
              text: {
                en: AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_PLACEHOLDER,
              },
              text_color: DEFAULT_DISPLAY_BANNER_CLAIM.text_color,
              background_color: [
                ...DEFAULT_DISPLAY_BANNER_CLAIM.background_color,
              ],
              border_color: DEFAULT_DISPLAY_BANNER_CLAIM.border_color,
            },
          },
        },
      ],
    };
  }

  return {
    campaign_type: DEFAULT_CAMPAIGN_TYPE,
    start_mode: "new",
    schedule_mode: DEFAULT_SCHEDULE_MODE,
    steps: [{}],
    bet_targeted_preset: null,
    award_frequency: "one_time",
    award_frequency_amount: "1",
    award_frequency_unit: "days",
  };
};

/**
 * True once the events catalog exposes `campaign_start` — the single
 * source of truth for the feature-flag gate (`bonus.campaign_start_trigger`).
 * Shared by the wizard's default trigger mode and the pill's visibility.
 */
export const hasCampaignStartEvent = (eventGroups: EventCategoryGroup[]): boolean =>
  flattenEvents(eventGroups).some((e) => e.event_key === CAMPAIGN_START_EVENT_KEY);

/**
 * Default `trigger_mode` for a brand-new campaign — `campaign_start` when the
 * catalog exposes it (flag on), otherwise `event` (BONENG-882 removed the
 * selectable "None" trigger).
 */
export const defaultTriggerModeFor = (eventGroups: EventCategoryGroup[]): WizardTriggerMode =>
  hasCampaignStartEvent(eventGroups) ? "campaign_start" : "event";

/** Duration, custom date range, or never for schedule selection. */
export type ScheduleDurationUnit = "hour" | "day";
export type ScheduleMode = `${number}h` | `${number}d` | "custom" | "never";

/** Default schedule for a brand-new campaign. */
export const DEFAULT_SCHEDULE_MODE: ScheduleMode = "4h";

/** Caps typed Run for amounts so `new Date(now + duration).toISOString()` cannot throw. */
export const MAX_SCHEDULE_DURATION_DAYS = 365;
export const MAX_SCHEDULE_DURATION_HOURS = MAX_SCHEDULE_DURATION_DAYS * 24;

export const clampScheduleDuration = (
  amount: number,
  unit: ScheduleDurationUnit,
): number => {
  const max =
    unit === "hour" ? MAX_SCHEDULE_DURATION_HOURS : MAX_SCHEDULE_DURATION_DAYS;
  return Math.min(amount, max);
};

const SCHEDULE_DURATION_PATTERN = /^(\d+)(h|d)$/;

export const parseScheduleDuration = (
  mode: string | undefined,
): { amount: number; unit: ScheduleDurationUnit } | undefined => {
  if (!mode) return undefined;
  const match = SCHEDULE_DURATION_PATTERN.exec(mode);
  if (!match) return undefined;
  const amount = Number(match[1]);
  if (!Number.isInteger(amount) || amount <= 0) return undefined;
  return { amount, unit: match[2] === "h" ? "hour" : "day" };
};

export const scheduleDurationMode = (
  amount: number,
  unit: ScheduleDurationUnit,
): Exclude<ScheduleMode, "custom" | "never"> => {
  const clamped = clampScheduleDuration(amount, unit);
  return unit === "hour" ? `${clamped}h` : `${clamped}d`;
};

/**
 * Human-readable label for a schedule mode (used in Review + sidebar summary
 * so the admin sees text rather than raw ISO timestamps for duration values).
 */
export const scheduleModeLabel = (mode: ScheduleMode | undefined): string => {
  switch (mode) {
    case "4h":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_4H;
    case "12h":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_12H;
    case "24h":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_24H;
    case "7d":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_7D;
    case "30d":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_30D;
    case "never":
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_NEVER_OPEN_ENDED;
    case "custom":
      return "";
    case undefined:
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_NEVER_OPEN_ENDED;
    default: {
      const parsed = parseScheduleDuration(mode);
      if (parsed?.unit === "hour") {
        return formatString(AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_N_HOURS, {
          n: String(parsed.amount),
        });
      }
      if (parsed?.unit === "day") {
        return formatString(AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_ENDS_N_DAYS, {
          n: String(parsed.amount),
        });
      }
      return AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_NEVER_OPEN_ENDED;
    }
  }
};

/**
 * Human-readable trigger label for the Review grid and the wizard sidebar
 * summary. `eventName` is the resolved catalog display name used for ordinary
 * event triggers; the sugared modes have fixed copy.
 */
export const triggerSummaryLabel = (
  mode: WizardTriggerMode | undefined,
  eventName: string,
): string => {
  if (mode === "none" || !mode) {
    return AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_SUMMARY_NONE;
  }
  if (mode === "referral") {
    return AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_FRIEND_REFERRAL;
  }
  if (mode === "affiliate_referral") {
    return AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_AFFILIATE_REFERRAL;
  }
  if (mode === "campaign_start") {
    return AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_SUMMARY_CAMPAIGN_START;
  }
  return eventName || AppStrings.PAM_DYNAMIC_BONUS_EMPTY_VALUE;
};

/** Display format for custom schedule dates (Review + sidebar summary). */
export const SCHEDULE_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

/** Formats an ISO schedule date in the admin's timezone, or an em dash when unset. */
export const formatScheduleDate = (iso?: string | null): string =>
  iso ? convertToUserTimezone(iso, SCHEDULE_DATE_TIME_FORMAT) : "—";

/** Freeform placement on the display-banner canvas (percentages 0–100). */
export type FreeformBannerLayout = {
  image: { x: number; y: number; width: number };
  button: {
    x: number;
    y: number;
    /** Button width as % of the banner. */
    width: number;
    /** Button height as % of the banner. */
    height: number;
  };
};

export const DEFAULT_FREEFORM_BANNER_LAYOUT: FreeformBannerLayout = {
  image: { x: 0, y: 0, width: 100 },
  button: { x: 72, y: 62, width: 24, height: 26 },
};

export const DEFAULT_DISPLAY_BANNER_CLAIM = {
  visible: true,
  text: { en: "Play now" },
  text_color: "#09090C",
  background_color: ["#FC4233", "#FCA732"],
  border_color: "#FD6F27",
} as const;

export type WizardStepFormValue = {
  event_key?: string;
  result_type_key?: string;
  result_params?: Record<string, unknown>;
  conditions?: { type: string; params?: Record<string, unknown> }[];
  banner?: BannerIn;
  /** BONENG-921 — undefined = not configured (backend classifies automatically). */
  display_locations?: DisplayLocation[];
  /**
   * Follow-up steps only (BONENG-962): the "Step 1 Promo Applied" toggle
   * controlling `applied_offer_matches` injection at submit. `undefined`
   * means "default on"; only an explicit `false` skips the condition.
   */
  applied_offer_matches_enabled?: boolean;
  /** UI-only names for sportsbook/casino restriction IDs (not submitted). */
  restriction_display_names?: Record<string, string>;
  /** BONENG-813 — image/button placement for standalone display banners. */
  freeform_layout?: FreeformBannerLayout;
};

export type WizardFormValues = {
  // Start
  campaign_type?: WizardCampaignType;
  start_mode?: "template" | "new";
  created_from_template_id?: string | null;
  name?: string;
  /** Template mode only — templates carry a description, campaigns don't. */
  description?: string;
  schedule_mode?: ScheduleMode;
  date_range?: [string | null, string | null];
  priority?: PriorityLevel;
  /**
   * Named bet-targeted blueprint (BONENG-896). When set, the wizard locks to
   * a fixed two-step campaign_start + sportsbook shape and re-injects the
   * preset's step-2 conditions at submit. Null/undefined = free-form campaign.
   */
  bet_targeted_preset?: WizardBetTargetedPreset | null;
  // Audience
  audience_mode?: "all" | "segment";
  segment_id?: string;
  // Trigger
  trigger_mode?: WizardTriggerMode;
  trigger_event_key?: string;
  affiliate_id?: string;
  affiliate_referral_code?: string;
  max_awards_per_user?: string;
  award_frequency?: AwardFrequency;
  award_frequency_amount?: string;
  award_frequency_unit?: AwardFrequencyUnit;
  award_frequency_exact_seconds?: number;
  // Steps
  steps?: WizardStepFormValue[];
  // Review
  save_as_template?: boolean;
  template_name?: string;
  // Hydrated template pass-throughs with no editing UI
  extra_eligibility?: ConditionSpecIn[];
  localized_strings?: Record<string, Record<string, string>>;
};

/** Steps are positional — derive a stable backend id from the index. */
export const stepIdForIndex = (index: number): string => `step_${index + 1}`;

/** The event_key step 1 fires on, resolved from the trigger selection. */
export const triggerEventKeyFor = (values: Pick<WizardFormValues, "trigger_mode" | "steps">): string => {
  if (values.trigger_mode === "referral" || values.trigger_mode === "affiliate_referral") {
    return REFERRAL_TRIGGER_EVENT_KEY;
  }
  if (values.trigger_mode === "campaign_start") {
    return CAMPAIGN_START_EVENT_KEY;
  }
  // "none", "event", or unset — fire on step 1's own event selection.
  return values.steps?.[0]?.event_key ?? "";
};

/**
 * Shared follow-up event: step 1's event in `event` mode, or the first
 * follow-up's own event when step 1 is `campaign_start`.
 */
export const followUpEventKeyFor = (
  values: Pick<WizardFormValues, "trigger_mode" | "steps">,
): string => {
  if (values.trigger_mode === "campaign_start") {
    return (values.steps ?? []).slice(1).find((s) => s.event_key)?.event_key ?? "";
  }
  return triggerEventKeyFor(values);
};

/** Resolved `event_key` each step will submit, matching `buildCampaignBody`. */
export const resolvedStepEventKeys = (
  values: Pick<WizardFormValues, "trigger_mode" | "steps">,
): string[] => {
  const stepOneEventKey = triggerEventKeyFor(values);
  const followUpFallback = followUpEventKeyFor(values);
  return (values.steps ?? []).map((step, index) =>
    index === 0 ? stepOneEventKey : step.event_key ?? followUpFallback,
  );
};

/** Count of steps whose catalog `result_type` is `award`. CHOICE is one step. */
export const awardStepCount = (
  steps: Array<{ result_type_key?: string } | undefined> | undefined,
  resolveResultType: (typeKey: string) => ResultType | "",
): number =>
  (steps ?? []).reduce((count, step) => {
    const key = step?.result_type_key ?? "";
    return resolveResultType(key) === "award" ? count + 1 : count;
  }, 0);

/**
 * Inline cap check mirroring the backend 422: a set cap must be at least
 * the AWARD-step count. Blank / uncap is an explicit operator choice.
 */
export const validateMaxAwardsPerUser = (
  raw: string | undefined,
  steps: Array<{ result_type_key?: string } | undefined> | undefined,
  resolveResultType: (typeKey: string) => ResultType | "",
): string | undefined => {
  if (raw == null || raw === "") return undefined;
  const parsed = parseInt(String(raw), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  const count = awardStepCount(steps, resolveResultType);
  if (count > 0 && parsed < count) {
    return formatString(AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_BELOW_STEPS, {
      count: String(count),
    });
  }
  return undefined;
};

/**
 * Step-1 condition types re-injected at submit by the referral trigger modes.
 * Stripped from hydrated template steps so submit doesn't duplicate them.
 */
export const REFERRAL_INJECTED_CONDITION_TYPES: ReadonlySet<string> = new Set([
  "user_was_referred",
  "referral_code_is_affiliate",
]);

/**
 * Recovers the trigger mode from a template's expanded step shapes. Templates
 * store fully expanded steps, so referral/affiliate campaigns carry
 * `event_key: "user_signup"` — the injected step-1 conditions are the only
 * reliable discriminator.
 */
const inferTriggerMode = (
  eventKey: string | undefined,
  conditions: ConditionSpecIn[],
): WizardTriggerMode => {
  if (eventKey === CAMPAIGN_START_EVENT_KEY) return "campaign_start";
  if (conditions.some((condition) => condition.type === "referral_code_is_affiliate")) {
    return "affiliate_referral";
  }
  if (conditions.some((condition) => condition.type === "user_was_referred")) {
    return "referral";
  }
  return eventKey ? "event" : "none";
};

export const inferTriggerModeFromSteps = (
  steps: WizardStepFormValue[],
): WizardTriggerMode =>
  inferTriggerMode(steps[0]?.event_key, steps[0]?.conditions ?? []);

/** Same recovery as {@link inferTriggerModeFromSteps} for API/list step specs. */
export const inferTriggerModeFromStepSpecs = (
  steps: Array<Pick<StepSpecIn, "trigger" | "conditions">>,
): WizardTriggerMode =>
  inferTriggerMode(
    steps[0]?.trigger?.event_key ?? undefined,
    steps[0]?.conditions ?? [],
  );

/**
 * Recovers the affiliate referral code embedded in a template's expanded
 * shapes (`referral_code_is_affiliate` on step 1, or
 * `campaign_affiliate_code_matches` in eligibility).
 */
export const affiliateCodeFromTemplate = (
  steps: WizardStepFormValue[],
  eligibility: ConditionSpecIn[],
): string | undefined => {
  const stepCondition = (steps[0]?.conditions ?? []).find(
    (c) => c.type === "referral_code_is_affiliate",
  );
  const eligibilityCondition = eligibility.find(
    (c) => c.type === "campaign_affiliate_code_matches",
  );
  const code = stepCondition?.params?.code ?? eligibilityCondition?.params?.code;
  return typeof code === "string" && code ? code : undefined;
};

/** Maps step specs into the wizard's step form values. */
export const toStepFormValues = (
  steps: StepSpecIn[],
  triggerMode: WizardTriggerMode = "campaign_start",
): WizardStepFormValue[] => {
  // Step 1's normalized result key decides whether follow-up steps qualify
  // for the "Step 1 Promo Applied" toggle (BONENG-962).
  const stepOneResultType = steps?.[0]?.results?.[0]?.type;
  const stepOneResultTypeKey =
    stepOneResultType === "show_banner" ? "show_bonus" : stepOneResultType;
  return (steps ?? []).map((step, index) => {
    const result = step.results?.[0];
    const legacyTitle = result?.params?.banner_title_key;
    const legacyMessage = result?.params?.banner_msg_key;
    const title = typeof legacyTitle === "string" ? legacyTitle : undefined;
    const shortDescription =
      typeof legacyMessage === "string" ? legacyMessage : undefined;
    const legacyOfferBanner =
      result?.result_type === "offer" && (title || shortDescription)
        ? {
            ...(title ? { title: { text: { en: title } } } : {}),
            ...(shortDescription
              ? { short_description: { text: { en: shortDescription } } }
              : {}),
          }
        : undefined;

    // The toggle only owns applied_offer_matches on follow-up steps that
    // qualify for it. On non-qualifying steps (e.g. a pre-BONENG-962 campaign
    // whose follow-up triggers on deposit_completed), the stored condition is
    // kept verbatim in the conditions array so a round-trip edit never
    // silently drops it — buildCampaignBody preserves it the same way.
    const toggleOwnsAppliedOffer =
      index > 0 &&
      appliedOfferToggleAvailable(
        stepOneResultTypeKey,
        step.trigger?.event_key ?? undefined,
      );

    return {
      event_key: step.trigger?.event_key ?? undefined,
      // Backend B1 keeps `show_banner` as a hidden alias; normalize so the
      // catalog-driven Result dropdown (which only lists `show_bonus`) matches.
      result_type_key:
        result?.type === "show_banner" ? "show_bonus" : result?.type,
      result_params: result?.params,
      // applied_offer_matches never appears in the conditions dropdown — where
      // the step-2 toggle owns it (BONENG-962), strip it from form state and
      // record its presence as the toggle's hydrated value instead.
      conditions: (step.conditions ?? [])
        .filter(
          (condition) =>
            !isCooldownCondition(condition) &&
            (!toggleOwnsAppliedOffer ||
              condition.type !== APPLIED_OFFER_MATCHES_TYPE),
        )
        .map((condition) => ({
          type: condition.type,
          params: condition.params,
        })),
      ...(toggleOwnsAppliedOffer
        ? {
            applied_offer_matches_enabled: (step.conditions ?? []).some(
              (condition) => condition.type === APPLIED_OFFER_MATCHES_TYPE,
            ),
          }
        : {}),
      banner: step.banner ?? legacyOfferBanner,
      // Preserve authored locations. Legacy steps without them use defaults
      // derived from their result and trigger.
      display_locations: step.display_locations?.length
        ? step.display_locations
        : isInformResultType(result?.result_type) || result?.result_type === "award"
          ? defaultDisplayLocations({
              resultTypeKey:
                result?.type === "show_banner" ? "show_bonus" : result?.type,
              eventKey: step.trigger?.event_key ?? undefined,
              triggerMode,
            })
          : undefined,
      ...(step.freeform_layout ? { freeform_layout: step.freeform_layout } : {}),
    };
  });
};

/**
 * Shared hydration for template/campaign step + eligibility shapes into wizard
 * form values (trigger recovery, referral stripping, audience segment).
 */
const hydrateSharedWizardValues = (
  steps: StepSpecIn[],
  eligibility: ConditionSpecIn[],
  caps?: CapSpecIn,
  localizedStrings?: Record<string, Record<string, string>>,
): Partial<WizardFormValues> => {
  const triggerMode = inferTriggerModeFromStepSpecs(steps);
  const stepValues = toStepFormValues(steps, triggerMode);
  const segmentCondition = eligibility.find((c) => c.type === "user_in_segment");
  const segmentId = segmentCondition?.params?.segment_id as string | undefined;

  const affiliateCode =
    triggerMode === "affiliate_referral"
      ? affiliateCodeFromTemplate(stepValues, eligibility)
      : undefined;
  if (triggerMode === "affiliate_referral") {
    // BONENG-959: affiliate step 1 is injected-only — drop any authored
    // free-form rows on hydrate so the form never shows conditions that
    // buildCampaignBody would silently strip on save.
    stepValues[0] = {
      ...stepValues[0],
      conditions: [],
    };
  } else if (triggerMode === "referral") {
    stepValues[0] = {
      ...stepValues[0],
      conditions: (stepValues[0].conditions ?? []).filter(
        (c) => !REFERRAL_INJECTED_CONDITION_TYPES.has(c.type),
      ),
    };
  }

  return {
    steps: stepValues.length ? stepValues : [{}],
    max_awards_per_user:
      caps?.max_awards_per_user != null ? String(caps.max_awards_per_user) : undefined,
    ...inferAwardFrequencyFields(caps, steps),
    extra_eligibility: eligibility.filter(
      (c) =>
        c.type !== "user_in_segment" &&
        c.type !== "not_already_awarded" &&
        !(
          triggerMode === "affiliate_referral" &&
          c.type === "campaign_affiliate_code_matches"
        ),
    ),
    localized_strings: localizedStrings,
    audience_mode: segmentId ? "segment" : "all",
    segment_id: segmentId,
    trigger_mode: triggerMode,
    affiliate_id: undefined,
    affiliate_referral_code: affiliateCode,
    // Do not infer a named preset from authored steps. The wizard seed
    // effect would treat a missing follow-up as "apply the blueprint" and
    // can wipe step 1 or restore a deleted step 2 (BONENG-1236).
    bet_targeted_preset: null,
  };
};

/** Maps a numeric or labelled campaign priority back to a wizard priority level. */
export const priorityLevelFromCampaign = (
  priority: CampaignOut["priority"],
): PriorityLevel | undefined => {
  if (typeof priority === "number") {
    const match = (Object.keys(PRIORITY_VALUES) as PriorityLevel[]).find(
      (level) => PRIORITY_VALUES[level] === priority,
    );
    return match;
  }
  if (typeof priority !== "string") return undefined;
  const normalized = priority.trim().toLowerCase();
  if (normalized === "low" || normalized === "medium" || normalized === "high") {
    return normalized;
  }
  return undefined;
};

/**
 * Maps a template into the wizard's form values — the single hydration path
 * shared by the Start step's "Use Template" prefill and the template edit
 * flow. Key order matters: callers apply the entries in order, mirroring the
 * original `setFieldValue` sequence.
 *
 * Templates store fully expanded steps, so referral/affiliate campaigns carry
 * `event_key: "user_signup"` plus the injected conditions. The trigger mode is
 * recovered from those conditions, then everything the trigger re-injects at
 * submit is stripped to avoid duplicates in the payload. Conditions without
 * wizard UI pass through as `extra_eligibility`; the segment one is owned by
 * the Audience step, and the affiliate-injected ones are re-derived from the
 * trigger selection. The affiliate itself isn't stored in the template — the
 * operator must re-select it — but the original code is surfaced as a
 * starting point.
 */
export const templateToWizardValues = (
  template: TemplateOut,
): Partial<WizardFormValues> => ({
  created_from_template_id: template.template_id,
  ...hydrateSharedWizardValues(
    template.default_steps ?? [],
    template.default_eligibility ?? [],
    template.default_caps,
    template.default_localized_strings,
  ),
  ...(template.default_steps?.[0]?.results?.[0]?.type ===
  DISPLAY_BANNER_RESULT_TYPE_KEY
    ? { campaign_type: DISPLAY_BANNER_CAMPAIGN_TYPE }
    : {}),
});

/**
 * Maps an existing campaign into wizard form values for the edit flow.
 * Schedule is hydrated as `custom` (or `never` when open-ended) so Save does
 * not rewrite the window relative to "now".
 */
export const campaignToWizardValues = (
  campaign: CampaignOut,
): Partial<WizardFormValues> => {
  const endsAt = campaign.ends_at ?? null;
  const schedule_mode: ScheduleMode = endsAt ? "custom" : "never";
  const priority = priorityLevelFromCampaign(campaign.priority);

  return {
    name: campaign.name,
    description: campaign.description ?? "",
    schedule_mode,
    date_range: [campaign.starts_at, endsAt],
    ...(priority ? { priority } : {}),
    created_from_template_id: campaign.created_from_template_id ?? null,
    ...hydrateSharedWizardValues(
      campaign.steps ?? [],
      campaign.eligibility_conditions ?? [],
      campaign.caps,
      campaign.localized_strings,
    ),
    campaign_type: isSeriesRunCampaign(campaign)
      ? SERIES_CAMPAIGN_TYPE
      : campaign.steps?.[0]?.results?.[0]?.type === DISPLAY_BANNER_RESULT_TYPE_KEY
        ? DISPLAY_BANNER_CAMPAIGN_TYPE
        : DEFAULT_CAMPAIGN_TYPE,
    // Prefer the campaign's top-level affiliate code when present.
    ...(campaign.affiliate_referral_code
      ? { affiliate_referral_code: campaign.affiliate_referral_code }
      : {}),
  };
};

/**
 * Maps a GET clone-preview (`CampaignIn`) into create-wizard values.
 * Forces `start_mode: "new"` so a series-stamped run does not reopen as Braze.
 */
export const campaignCloneToWizardValues = (
  clone: CampaignIn,
): Partial<WizardFormValues> => ({
  ...campaignToWizardValues({
    ...clone,
    campaign_id: "",
    status: "draft",
    created_at: clone.starts_at,
    updated_at: clone.starts_at,
  }),
  start_mode: "new",
});

/**
 * Maps a GET clone-preview (`TemplateIn`) into template-wizard values.
 * Name/description come from the clone (already `{name} (Copy)`); do not treat
 * this as "create a campaign from this template".
 */
export const templateCloneToWizardValues = (
  clone: TemplateIn,
): Partial<WizardFormValues> => ({
  ...templateToWizardValues({
    ...clone,
    template_id: "",
    created_at: "",
    updated_at: "",
  }),
  name: clone.name,
  description: clone.description ?? "",
  created_from_template_id: null,
  start_mode: "new",
});

/**
 * Maps an existing series into wizard form values for the edit flow.
 * Forces the series blueprint (never expires, campaign_start, all-users
 * audience) regardless of what hydrateSharedWizardValues infers from steps.
 */
export const seriesToWizardValues = (
  series: SeriesOut,
): Partial<WizardFormValues> => {
  const priority = priorityLevelFromCampaign(series.priority);
  const shared = hydrateSharedWizardValues(
    series.steps ?? [],
    series.eligibility_conditions ?? [],
    series.caps,
    series.localized_strings,
  );

  return {
    ...shared,
    campaign_type: SERIES_CAMPAIGN_TYPE,
    start_mode: "new",
    name: series.name,
    schedule_mode: "never",
    date_range: [series.starts_at, null],
    trigger_mode: "campaign_start",
    audience_mode: "all",
    segment_id: undefined,
    ...(priority ? { priority } : {}),
    created_from_template_id: series.created_from_template_id ?? null,
  };
};

/**
 * Step-1 conditions injected by the referral trigger types. Mirrors what
 * `CreateAffiliateCampaignPage.onFinish` hardcodes today. Rendered as
 * non-removable badges in the Steps step; merged into the payload here.
 */
export const injectedStepConditionsFor = (
  triggerMode: WizardTriggerMode | undefined,
  affiliateCode: string | undefined,
): ConditionSpecIn[] => {
  if (triggerMode === "referral") {
    return [{ type: "user_was_referred" }];
  }
  if (triggerMode === "affiliate_referral") {
    return [
      { type: "user_was_referred" },
      ...(affiliateCode
        ? [{ type: "referral_code_is_affiliate", params: { code: affiliateCode } }]
        : []),
    ];
  }
  return [];
};

/** Campaign-level eligibility conditions injected by the affiliate trigger type. */
export const injectedEligibilityFor = (
  triggerMode: WizardTriggerMode | undefined,
  affiliateCode: string | undefined,
): ConditionSpecIn[] => {
  if (triggerMode !== "affiliate_referral") return [];
  // Duplicate awards are gated by caps.max_awards_per_user (default 1), not
  // the retired not_already_awarded condition (BONENG-1031).
  return affiliateCode
    ? [{ type: "campaign_affiliate_code_matches", params: { code: affiliateCode } }]
    : [];
};

// AntD preserves optional blank inputs as empty strings/undefined. The API
// interprets omitted params as "use default/no limit", so drop blank values.
const DATETIME_LOCAL_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const toUtcIsoDateTime = (value: string): string => {
  if (!DATETIME_LOCAL_RE.test(value)) {
    return value;
  }
  const ms = parseAuthoringDateMs(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : value;
};

const omitBlankResultParams = (
  params?: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params ?? {})
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => {
        if (key === "expires_at" && typeof value === "string") {
          return [key, toUtcIsoDateTime(value)];
        }
        return [key, value];
      }),
  );

/**
 * True when `iso` is a valid timestamp at or before `now` — used to lock an
 * already-started campaign's start so schedule edits can't rewrite it.
 */
export const isPastOrPresentIso = (
  iso: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!iso) return false;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) && ms <= now.getTime();
};

/**
 * Resolve `starts_at` / `ends_at` from the wizard's schedule selection.
 *
 * Duration modes compute the window relative to `now` at submit time so the
 * admin is not surprised by a stale end time from when they opened the wizard.
 * "Custom" defers to the date_range the admin entered. "Never" (and any
 * unrecognised mode) is open-ended: use a hydrated start from date_range when
 * present (edit flow), otherwise start now (create flow).
 *
 * A start that is already in the past is never rewritten — duration modes
 * keep that start and only move `ends_at` relative to `now`.
 */
export const resolveSchedule = (
  scheduleMode: ScheduleMode | undefined,
  dateRange: [string | null, string | null] | undefined,
  now: Date = new Date(),
): { starts_at: string; ends_at: string | null } => {
  const nowIso = now.toISOString();
  const lockedStart =
    dateRange?.[0] && isPastOrPresentIso(dateRange[0], now) ? dateRange[0] : null;

  const parsedDuration = parseScheduleDuration(scheduleMode);
  if (parsedDuration) {
    const amount = clampScheduleDuration(
      parsedDuration.amount,
      parsedDuration.unit,
    );
    const msPerHour = 60 * 60 * 1000;
    const durationMs =
      parsedDuration.unit === "hour"
        ? amount * msPerHour
        : amount * 24 * msPerHour;
    return {
      starts_at: lockedStart ?? nowIso,
      ends_at: new Date(now.getTime() + durationMs).toISOString(),
    };
  }

  if (scheduleMode === "custom") {
    return {
      starts_at: lockedStart ?? dateRange?.[0] ?? nowIso,
      ends_at: dateRange?.[1] ?? null,
    };
  }

  // "never" or unset -> open-ended; keep a hydrated/locked start on edit.
  return { starts_at: lockedStart ?? dateRange?.[0] ?? nowIso, ends_at: null };
};

/**
 * Pure builder: expands the wizard's form values into the `CampaignIn` body.
 * All referral/affiliate expansion lives here so it is unit-testable without
 * rendering the wizard.
 *
 * `now` is injectable for deterministic tests (defaults to `new Date()`).
 */
export const buildCampaignBody = (
  values: WizardFormValues,
  resolveResultType: (typeKey: string) => ResultType | "",
  now: Date = new Date(),
): CampaignIn => {
  const triggerMode = values.trigger_mode ?? "none";
  const affiliateCode =
    triggerMode === "affiliate_referral" ? values.affiliate_referral_code : undefined;
  const stepOneEventKey = triggerEventKeyFor({ ...values, trigger_mode: triggerMode });

  // `campaign_start` may only appear on step 1 — follow-up fallback is the
  // first follow-up step's own selection, never `campaign_start` itself.
  const followUpEventKeyFallback = followUpEventKeyFor({
    ...values,
    trigger_mode: triggerMode,
  });

  // Informational banners transfer no value — skip award-frequency caps even
  // when the v2 path keeps campaign_type as "campaign" (BONENG-1189). Mixed
  // banner + award campaigns still compile frequency for the award step.
  const compiledFrequency =
    values.campaign_type === DISPLAY_BANNER_CAMPAIGN_TYPE ||
    isInformationalBannerOnly(values.steps)
      ? { caps: undefined, cooldownSeconds: null }
      : compileAwardFrequency({
          award_frequency: values.award_frequency,
          award_frequency_amount: values.award_frequency_amount,
          award_frequency_unit: values.award_frequency_unit,
          award_frequency_exact_seconds: values.award_frequency_exact_seconds,
          max_awards_per_user: values.max_awards_per_user,
          applyAffiliateCapDefault: triggerMode === "affiliate_referral",
          awardStepCount: awardStepCount(values.steps, resolveResultType),
        });

  const steps: StepSpecIn[] = (values.steps ?? []).map((step, index) => {
    const isFirst = index === 0;
    // Soft-retired from authoring (BONENG-1031) — drop lingering copies on
    // edit-resave so old campaigns shed the type.
    const userConditions = (step.conditions ?? []).filter(
      (c) => c.type && c.type !== "not_already_awarded",
    );
    const resultTypeKey = step.result_type_key ?? "";
    const eventKey = isFirst
      ? stepOneEventKey
      : step.event_key ?? followUpEventKeyFallback;
    // Follow-up steps: inject applied_offer_matches when the step qualifies
    // for the toggle and the operator left it on (BONENG-962). Strip form
    // copies of that type so the payload doesn't duplicate it — except on
    // steps where the toggle is unavailable: a stored copy (pre-BONENG-962
    // campaign on a non-sportsbook trigger) passes through verbatim so a
    // round-trip edit never silently drops it. Preset seed conditions are
    // ordinary form rows (BONENG-984) and ship as authored.
    const followUpInjectedTypes = followUpInjectedConditionTypes(
      appliedOfferToggleAvailable(values.steps?.[0]?.result_type_key, eventKey),
    );
    // BONENG-959: Basic (campaign_start) step 1 never persists authored
    // conditions. Affiliate referral step 1 ships only the injected referral /
    // affiliate-code set — any free-form rows are stripped.
    const injectedFirst = injectedStepConditionsFor(triggerMode, affiliateCode);
    const firstStepConditions =
      triggerMode === "campaign_start"
        ? []
        : triggerMode === "affiliate_referral"
          ? injectedFirst
          : [...injectedFirst, ...userConditions];
    const conditions = isFirst
      ? firstStepConditions
      : [
          ...injectedFollowUpConditionsFor(
            values.steps?.[0]?.result_type_key,
            eventKey,
            step.applied_offer_matches_enabled,
          ),
          ...userConditions.filter((c) => !followUpInjectedTypes.has(c.type)),
        ];
    const resultType = resolveResultType(resultTypeKey);
    const conditionsWithFrequency = withAwardFrequencyCooldown(
      conditions,
      resultType === "award",
      compiledFrequency.cooldownSeconds,
    );
    const isDisplayBannerResult =
      resultTypeKey === DISPLAY_BANNER_RESULT_TYPE_KEY;
    const supportsBanner =
      isDisplayBannerResult ||
      supportsStepBannerEditor(resultTypeKey, resultType);
    const banner = supportsBanner ? pruneBanner(step.banner) : undefined;
    const resultParams = stripInactiveConditionalResultParams(
      resultTypeKey,
      omitBlankResultParams(step.result_params),
    );
    delete resultParams[RESTRICTION_DISPLAY_NAMES_PARAM_KEY];
    // BONENG-913: percent_of_trigger sizes the reward from the trigger
    // event's monetary amount. If the operator set a percent and then
    // switched to an event with no base amount, the hidden input's stale
    // value must not submit — the backend would 422 (percent alongside the
    // now-required $ amount) or author a campaign that never awards.
    if (!PERCENT_OF_TRIGGER_EVENT_KEYS.has(eventKey)) {
      delete resultParams[PERCENT_OF_TRIGGER_PARAM_KEY];
    }
    // BONENG-960: an untouched transaction title is dropped so the backend
    // resolves its localized default — sending PAM's English literal would show
    // English in every other locale.
    if (
      isDefaultTransactionTitle(
        resultTypeKey,
        resultParams[TRANSACTION_TITLE_PARAM_KEY],
      )
    ) {
      delete resultParams[TRANSACTION_TITLE_PARAM_KEY];
    }
    // BONENG-976: max_amount only caps a percent-of-trigger reward. If the
    // percent didn't survive to the payload (blank, or stripped above), a
    // stale cap must not submit — the backend 422s max_amount without
    // percent_of_trigger.
    if (resultParams[PERCENT_OF_TRIGGER_PARAM_KEY] === undefined) {
      delete resultParams[MAX_AMOUNT_PARAM_KEY];
    } else {
      // BONENG-1171: percent sizing replaces the fixed-$ field. A leftover
      // amount can sit in the store after a Result switch (the input is
      // unmounted); the backend 422s configs that carry both.
      for (const param of RESULT_PARAM_SCHEMA[resultTypeKey] ?? []) {
        if (param.replacedByPercent) delete resultParams[param.key];
      }
    }
    // BONENG-1169: Boost Percentage is authored only on Profit Boost. Stale
    // values from edit hydrate or switching Result must not round-trip on
    // show_bonus and other result types.
    if (resultTypeKey !== "create_profit_boost_promotion") {
      delete resultParams.percentage;
    }
    // BONENG-912: rollover / per-step expiry are no longer authorable —
    // expiration lives on the campaign schedule. Hydrated legacy params
    // would otherwise round-trip on edit-resave; scrub them here only for
    // the sportsbook result types that used to carry them.
    if (REMOVED_STEP_PARAM_RESULT_KEYS.has(resultTypeKey)) {
      for (const key of REMOVED_STEP_PARAM_KEYS) {
        delete resultParams[key];
      }
    }
    const bannerTitle = banner?.title?.text?.en?.trim();
    const bannerMessage = banner?.short_description?.text?.en?.trim();
    const params =
      resultType === "offer"
        ? {
            ...resultParams,
            ...(bannerTitle ? { banner_title_key: bannerTitle } : {}),
            ...(bannerMessage ? { banner_msg_key: bannerMessage } : {}),
          }
        : resultParams;
    // Catalog-driven; fall back to info so a missing catalog entry still
    // submits a valid inform result_type instead of an empty string (422).
    const resolvedResultType: ResultType | "" = isDisplayBannerResult
      ? resultType || "info"
      : resultType;
    return {
      step_id: stepIdForIndex(index),
      trigger: {
        event_key: eventKey || null,
      },
      conditions: conditionsWithFrequency,
      results: resultTypeKey
        ? [
            {
              type: resultTypeKey,
              result_type: resolvedResultType as ResultType,
              params,
            },
          ]
        : [],
      ...(banner ? { banner } : {}),
      ...(supportsBanner && step.display_locations?.length
        ? { display_locations: step.display_locations }
        : {}),
      ...(isDisplayBannerResult && step.freeform_layout
        ? { freeform_layout: step.freeform_layout }
        : {}),
    };
  });

  const injectedEligibility = injectedEligibilityFor(triggerMode, affiliateCode);
  const injectedTypes = new Set(injectedEligibility.map((c) => c.type));
  const eligibility: ConditionSpecIn[] = [
    // Template-hydrated conditions with no wizard UI pass through, minus any the
    // trigger type re-injects (avoids duplicates), minus user_in_segment
    // (owned by the Audience step), and minus soft-retired not_already_awarded
    // (BONENG-1031 — edit-resave sheds lingering copies).
    ...(values.extra_eligibility ?? []).filter(
      (c) =>
        c.type &&
        c.type !== "user_in_segment" &&
        c.type !== "not_already_awarded" &&
        !injectedTypes.has(c.type),
    ),
    ...(values.audience_mode === "segment" && values.segment_id
      ? [{ type: "user_in_segment", params: { segment_id: values.segment_id } }]
      : []),
    ...injectedEligibility,
  ];

  const caps = compiledFrequency.caps;

  const schedule = resolveSchedule(values.schedule_mode, values.date_range, now);

  return {
    name: values.name ?? "",
    category: "standard",
    starts_at: schedule.starts_at,
    ends_at: schedule.ends_at,
    priority: values.priority ? PRIORITY_VALUES[values.priority] : PRIORITY_VALUES[DEFAULT_PRIORITY_LEVEL],
    steps,
    ...(eligibility.length ? { eligibility_conditions: eligibility } : {}),
    ...(caps ? { caps } : {}),
    ...(affiliateCode ? { affiliate_referral_code: affiliateCode } : {}),
    ...(values.localized_strings ? { localized_strings: values.localized_strings } : {}),
    created_from_template_id: values.created_from_template_id ?? null,
  };
};

/**
 * Pure builder: expands the wizard's form values into the `SeriesIn` body
 * (BONENG-842). A series always fires its fixed step 1 on `campaign_start`
 * and never expires — both are forced here regardless of what the (hidden,
 * for series) trigger/schedule fields hold, so the flow can't drift from the
 * backend's blueprint. Steps map through verbatim: usually two (OFFER +
 * AWARD), but a single campaign_start AWARD step is also valid (BONENG-1023,
 * `TriggerStepsStep`'s auto step-management effect keeps `values.steps` at
 * the right length for whichever shape step 1 resolves to). Audience is
 * forced to all users (series are subscription-triggered; the Audience step
 * is omitted). Fields upstream `SeriesIn` forbids (`ends_at`,
 * `affiliate_referral_code`, `category`, `referral_config`,
 * `localized_strings`) are stripped so the POST body matches `extra="forbid"`.
 *
 * `now` is injectable for deterministic tests (defaults to `new Date()`).
 */
export const buildSeriesBody = (
  values: WizardFormValues,
  resolveResultType: (typeKey: string) => ResultType | "",
  now: Date = new Date(),
): SeriesIn => {
  const campaign = buildCampaignBody(
    {
      ...values,
      trigger_mode: "campaign_start",
      schedule_mode: "never",
      audience_mode: "all",
      segment_id: undefined,
    },
    resolveResultType,
    now,
  );
  const {
    ends_at: _ends_at,
    affiliate_referral_code: _affiliate_referral_code,
    category: _category,
    referral_config: _referral_config,
    localized_strings: _localized_strings,
    ...seriesFields
  } = campaign;
  return seriesFields;
};

/**
 * "Save as template" body derived from the campaign just submitted — the same
 * shapes, re-labelled to the template contract.
 *
 * The template schema has no top-level `affiliate_referral_code`; for
 * affiliate campaigns the code travels inside the expanded shapes
 * (`campaign_affiliate_code_matches` in eligibility,
 * `referral_code_is_affiliate` on step 1) and is recovered on hydrate via
 * `affiliateCodeFromTemplate`.
 */
export const buildTemplateBody = (
  templateName: string,
  campaign: CampaignIn,
  description: string = `Created from campaign "${campaign.name}"`,
): TemplateIn => ({
  name: templateName,
  description,
  default_steps: campaign.steps,
  ...(campaign.caps ? { default_caps: campaign.caps } : {}),
  ...(campaign.eligibility_conditions?.length
    ? { default_eligibility: campaign.eligibility_conditions }
    : {}),
  ...(campaign.localized_strings
    ? { default_localized_strings: campaign.localized_strings }
    : {}),
});

/**
 * Pure builder for the wizard's template mode: expands the wizard values
 * through the campaign shape (the single source of truth for step, eligibility,
 * and cap expansion), then re-labels to the template contract. Schedule and
 * priority are campaign-only and drop out naturally — templates don't carry
 * them.
 */
export const buildTemplateIn = (
  values: WizardFormValues,
  resolveResultType: (typeKey: string) => ResultType | "",
): TemplateIn =>
  buildTemplateBody(
    values.name ?? "",
    buildCampaignBody(values, resolveResultType),
    values.description ?? "",
  );
